<!-- src/views/vehicles/VehicleCheckFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left text-[#0A1630]">
    
    <!-- 1. PREMIUM HEADER CARD -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 no-print">
      <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-12 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden">
        <div class="absolute top-0 right-0 p-12 opacity-5">
          <TruckIcon class="h-32 w-32 text-white" />
        </div>
        
        <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">Fleet Audit</h1>
            <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-3">Safety Check & Condition Reporting Protocol</p>
          </div>
          
          <router-link to="/dashboard" class="bg-white/5 border border-white/10 text-white px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-all flex items-center gap-2 shadow-lg active:scale-95">
            <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Abort Check</span>
          </router-link>
        </div>
      </div>
    </div>

    <!-- 2. BODY CONTENT -->
    <div class="max-w-7xl mx-auto px-4 sm:p-6 lg:px-8 py-10 pb-32">
      
      <!-- LOADING / SUCCESS STATES -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-24 bg-slate-50 rounded-[40px] border border-dashed border-slate-200 shadow-inner animate-pulse">
        <svg class="animate-spin h-10 w-10 text-[#20FCF1]" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
        <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-6">Authorising Asset Registry Access...</p>
      </div>

      <div v-else-if="successMessage" class="bg-[#EBFDF8] border-2 border-[#20FCF1]/20 p-16 rounded-[40px] text-center animate-fadeIn shadow-xl">
        <div class="bg-white w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl border border-[#20FCF1]/20">
          <CheckCircleIcon class="h-10 w-10 text-[#20FCF1]" />
        </div>
        <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">Fleet Registry Synchronised</h3>
        <p class="text-slate-500 text-xs font-bold uppercase tracking-widest mt-3 max-w-sm mx-auto leading-relaxed">
          The condition report for {{ selectedVehicle?.registration }} has been successfully logged.
        </p>
        <p class="text-[9px] text-[#20FCF1] font-black uppercase tracking-[0.2em] mt-8 animate-pulse">Redirecting to Dashboard...</p>
      </div>

      <!-- MAIN FORM -->
      <form v-else @submit.prevent="submitCheck" class="space-y-12 animate-fadeIn">
        
        <!-- ASSET SELECTOR (Styling Synced to Time Tracker) -->
        <div class="bg-slate-50 p-8 sm:p-10 rounded-[40px] border border-slate-100 shadow-inner flex flex-col md:flex-row items-center gap-10">
            <!-- Visual Asset Preview -->
            <div class="h-32 w-52 bg-white rounded-[32px] flex items-center justify-center overflow-hidden border border-slate-200 shrink-0 shadow-md">
              <img v-if="selectedVehicle?.imageUrl" :src="selectedVehicle.imageUrl" class="h-full w-full object-cover" alt="Asset" />
              <TruckIcon v-else class="h-12 w-12 text-slate-200" />
            </div>

            <!-- Identity Picker -->
            <div class="flex-grow w-full space-y-4">
              <label class="text-[10px] font-black text-[#0A1630] uppercase tracking-[0.2em] ml-1">Asset Allocation Selection</label>
              <div class="relative">
                <select v-model="selectedVehicleId" class="premium-select !bg-white">
                  <option v-for="vehicle in fleet" :key="vehicle.id" :value="vehicle.id">
                    {{ vehicle.registration }} — {{ vehicle.make }} {{ vehicle.model }}
                  </option>
                </select>
              </div>
              <p class="text-[9px] text-slate-400 font-bold uppercase tracking-widest ml-1 leading-relaxed">
                Ensure the registration matches the physical vehicle before confirming the audit handshake.
              </p>
            </div>
        </div>

        <!-- SECTION 1: EXTERNAL AUDIT -->
        <div class="bg-white rounded-[40px] border border-slate-100 shadow-xl overflow-hidden text-left">
          <div class="bg-slate-50 px-10 py-6 border-b border-slate-100 flex justify-between items-center">
            <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-widest">1. External Condition Audit</h3>
            <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest">Pass / Fail Protocol</span>
          </div>
          <div class="p-10 space-y-2">
            <CheckToggle v-for="item in externalChecks" :key="item.key" :label="item.label" v-model="formData.external[item.key]" />
            
            <div v-if="hasFailures('external')" class="mt-8 p-8 bg-rose-50 rounded-[32px] border border-rose-100 animate-fadeIn">
              <label class="text-[10px] font-black text-rose-600 mb-4 block ml-1 uppercase tracking-widest">External Defect Registry</label>
              <textarea v-model="formData.externalFaultDetails" rows="4" class="form-input !bg-white !border-rose-100" placeholder="Please provide specific details of the fault(s)..."></textarea>
              <div class="mt-6 flex items-center gap-4">
                <label class="flex-1 bg-white border-2 border-dashed border-rose-200 p-6 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:bg-rose-100/50 transition-all group">
                  <PhotoIcon class="h-6 w-6 text-rose-400 mb-2 group-hover:scale-110 transition-transform" />
                  <span class="text-[10px] font-black text-rose-500 uppercase tracking-widest">Upload Condition Evidence</span>
                  <input type="file" accept="image/*" @change="(e) => handleFileUpload(e, 'external')" class="hidden"/>
                </label>
                <div v-if="files.external" class="bg-[#20FCF1] text-[#0A1630] px-5 py-3 rounded-xl text-[9px] font-black uppercase flex items-center gap-2 shadow-lg">
                  <CheckCircleIcon class="h-3 w-3" /> File Synchronised
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- SECTION 2: INTERNAL AUDIT -->
        <div class="bg-white rounded-[40px] border border-slate-100 shadow-xl overflow-hidden text-left">
          <div class="bg-slate-50 px-10 py-6 border-b border-slate-100 flex justify-between items-center">
            <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-widest">2. Cabin & Controls Audit</h3>
            <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest">Pass / Fail Protocol</span>
          </div>
          <div class="p-10 space-y-2">
            <CheckToggle v-for="item in internalChecks" :key="item.key" :label="item.label" v-model="formData.internal[item.key]" />

            <div v-if="hasFailures('internal')" class="mt-8 p-8 bg-rose-50 rounded-[32px] border border-rose-100 animate-fadeIn text-left">
              <label class="text-[10px] font-black text-rose-600 mb-4 block ml-1 uppercase tracking-widest">Internal Defect Registry</label>
              <textarea v-model="formData.internalFaultDetails" rows="4" class="form-input !bg-white !border-rose-100" placeholder="Please describe the cabin/control defects..."></textarea>
              <div class="mt-6 flex items-center gap-4">
                <label class="flex-1 bg-white border-2 border-dashed border-rose-200 p-6 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:bg-rose-100/50 transition-all group">
                  <PhotoIcon class="h-6 w-6 text-rose-400 mb-2 group-hover:scale-110 transition-transform" />
                  <span class="text-[10px] font-black text-rose-500 uppercase tracking-widest">Upload Cabin Evidence</span>
                  <input type="file" accept="image/*" @change="(e) => handleFileUpload(e, 'internal')" class="hidden"/>
                </label>
                <div v-if="files.internal" class="bg-[#20FCF1] text-[#0A1630] px-5 py-3 rounded-xl text-[9px] font-black uppercase flex items-center gap-2 shadow-lg">
                  <CheckCircleIcon class="h-3 w-3" /> File Synchronised
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- SECTION 3: MILEAGE -->
        <div class="bg-white rounded-[40px] border border-slate-100 shadow-xl overflow-hidden text-left">
          <div class="bg-slate-50 px-10 py-6 border-b border-slate-100 text-left">
            <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-widest">3. Odometer Verification</h3>
          </div>
          <div class="p-10">
            <label class="text-[10px] font-black mb-4 block ml-1 text-[#20FCF1] uppercase tracking-widest">Current Vehicle Mileage <span class="text-rose-500">*</span></label>
            <div class="relative max-w-sm">
              <input type="number" v-model.number="formData.mileage" required class="form-input text-2xl font-black font-mono tracking-tighter pr-16" placeholder="000000" />
              <span class="absolute right-6 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-300 uppercase tracking-widest">Miles</span>
            </div>
            <div class="mt-6 flex items-center gap-4 bg-slate-50 p-5 rounded-2xl border border-slate-100 inline-flex shadow-sm">
               <div class="bg-white p-2 rounded-lg shadow-sm border border-slate-100"><ClockIcon class="h-4 w-4 text-[#20FCF1]" /></div>
               <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                 Registry Log: <span class="text-[#0A1630] ml-2">{{ selectedVehicle?.mileage ? selectedVehicle.mileage.toLocaleString() : '0' }} MI</span>
               </p>
            </div>
          </div>
        </div>

        <!-- SECTION 4: LEGAL DECLARATIONS -->
        <div class="bg-[#0A1630] p-10 rounded-[40px] shadow-2xl text-white relative overflow-hidden text-left">
          <div class="absolute -right-10 -bottom-10 opacity-5"><ShieldCheckIcon class="h-64 w-64 text-[#20FCF1]" /></div>
          <h3 class="text-sm font-black text-[#20FCF1] uppercase tracking-widest mb-10 flex items-center gap-3 leading-none">
             <div class="w-2 h-2 rounded-full bg-[#20FCF1] animate-pulse"></div>
             Operator Synchronisation & Declaration
          </h3>
          <div class="space-y-8 relative z-10">
            <div class="flex items-start group cursor-pointer" @click="formData.licenceUnchanged = !formData.licenceUnchanged">
              <div class="relative flex items-center shrink-0">
                <input type="checkbox" v-model="formData.licenceUnchanged" @click.stop class="h-8 w-8 text-[#20FCF1] bg-white/5 border-white/20 rounded-xl focus:ring-[#20FCF1] transition-all cursor-pointer">
              </div>
              <label class="ml-6 text-xs font-bold text-slate-400 uppercase leading-relaxed tracking-widest group-hover:text-white transition-colors cursor-pointer">
                I verify that my <strong class="text-[#20FCF1]">Driving Licence</strong> remains valid and endorsements remain unchanged.
              </label>
            </div>
            <div class="flex items-start group cursor-pointer" @click="formData.fitToDrive = !formData.fitToDrive">
              <div class="relative flex items-center shrink-0">
                <input type="checkbox" v-model="formData.fitToDrive" @click.stop class="h-8 w-8 text-[#20FCF1] bg-white/5 border-white/20 rounded-xl focus:ring-[#20FCF1] transition-all cursor-pointer">
              </div>
              <label class="ml-6 text-xs font-bold text-slate-400 uppercase leading-relaxed tracking-widest group-hover:text-white transition-colors cursor-pointer">
                I formally attest that I am <strong class="text-[#20FCF1]">Fit to Operate</strong> this asset and follow all company A&D protocols.
              </label>
            </div>
          </div>
        </div>

        <!-- SUBMIT -->
        <div class="pt-10 flex flex-col sm:flex-row justify-end items-center gap-8">
          <button type="button" @click="$router.push('/dashboard')" class="text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-rose-500 transition-colors">Terminate Protocol</button>
          <button type="submit" :disabled="isSubmitting || !isFormValid" class="bg-[#20FCF1] text-[#0A1630] px-16 py-8 rounded-[32px] text-xl font-black uppercase tracking-[0.15em] shadow-2xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 min-w-[360px]">
            <span v-if="isSubmitting" class="flex items-center justify-center gap-4">
                <svg class="animate-spin h-6 w-6" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                Authorising...
            </span>
            <span v-else>Confirm Audit Entry</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, defineComponent, h } from 'vue';
import { useRouter } from 'vue-router';
import { db, storage, auth } from '@/firebaseConfig';
import { collection, getDocs, doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useUserStore } from '@/stores/userStore';
import { 
  TruckIcon, ArrowUturnLeftIcon, PhotoIcon, 
  ShieldCheckIcon, CheckCircleIcon, ClockIcon
} from '@heroicons/vue/24/outline';

const CheckToggle = defineComponent({
  props: ['label', 'modelValue'],
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    return () => h('div', { class: 'flex items-center justify-between p-5 rounded-2xl hover:bg-slate-50 transition-colors group' }, [
      h('span', { class: 'text-xs font-black text-[#0A1630] uppercase tracking-tight' }, props.label),
      h('button', {
        type: 'button',
        class: `relative inline-flex h-8 w-16 flex-shrink-0 cursor-pointer rounded-full border-4 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${props.modelValue ? 'bg-[#20FCF1]' : 'bg-slate-200'}`,
        onClick: () => emit('update:modelValue', !props.modelValue)
      }, [
        h('span', { class: `pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-xl ring-0 transition duration-200 ease-in-out ${props.modelValue ? 'translate-x-8' : 'translate-x-0'}` }),
        h('span', { class: `absolute ${props.modelValue ? 'left-2' : 'right-2'} top-1/2 -translate-y-1/2 text-[8px] font-black uppercase text-[#0A1630]` }, props.modelValue ? 'PASS' : 'FAIL')
      ])
    ]);
  }
});

const router = useRouter();
const userStore = useUserStore();

const loading = ref(true);
const isSubmitting = ref(false);
const successMessage = ref(false);
const error = ref('');

// FLEET REGISTRY STATE
const fleet = ref([]);
const selectedVehicleId = ref(null);

const selectedVehicle = computed(() => {
    return fleet.value.find(v => v.id === selectedVehicleId.value) || null;
});

const formData = reactive({
  external: { lights: true, brakeLights: true, tyres: true, spraySuppression: true, battery: true, bodyDoors: true, adBlue: true, fuelLeaks: true, engineNoise: true },
  internal: { horn: true, steering: true, seatbelts: true, wipers: true, mirrors: true, dashboard: true, brakes: true, security: true },
  externalFaultDetails: '', internalFaultDetails: '',
  mileage: null, licenceUnchanged: false, fitToDrive: false,
});

const files = reactive({ external: null, internal: null });

const externalChecks = [
  { key: 'lights', label: 'External Lights & Indicators' },
  { key: 'brakeLights', label: 'Brake Integrity & Trailer Connections' },
  { key: 'tyres', label: 'Tyre Condition & Wheel Fixings' },
  { key: 'spraySuppression', label: 'Spray Suppression Systems' },
  { key: 'battery', label: 'Battery Security & Terminals' },
  { key: 'bodyDoors', label: 'Structural Integrity (Body/Doors)' },
  { key: 'adBlue', label: 'AdBlue / Fluid Levels' },
  { key: 'fuelLeaks', label: 'Fuel, Oil & Coolant Leaks' },
  { key: 'engineNoise', label: 'Engine Performance & Emissions' },
];

const internalChecks = [
  { key: 'horn', label: 'Audible Warning (Horn)' },
  { key: 'steering', label: 'Steering Control Integrity' },
  { key: 'seatbelts', label: 'Seatbelt Operation & Condition' },
  { key: 'wipers', label: 'Wipers, Washers & Vision' },
  { key: 'mirrors', label: 'Mirror Alignment & Cameras' },
  { key: 'dashboard', label: 'System Fault Warnings (Dashboard)' },
  { key: 'brakes', label: 'Brake Build-up & Operation' },
  { key: 'security', label: 'Cabin Security (Doors/Steps)' },
];

const hasFailures = (section) => Object.values(formData[section]).some(val => val === false);

const isFormValid = computed(() => {
  if (!selectedVehicleId.value) return false;
  if (!formData.mileage) return false;
  if (!formData.licenceUnchanged || !formData.fitToDrive) return false;
  if (hasFailures('external') && !formData.externalFaultDetails) return false;
  if (hasFailures('internal') && !formData.internalFaultDetails) return false;
  return true;
});

const fetchFleetData = async () => {
  try {
    const profile = userStore.userProfile;
    const cid = userStore.clientId || profile?.clientId;
    if (!cid) return;
    
    const fleetSnap = await getDocs(collection(db, 'clients', cid, 'vehicles'));
    fleet.value = fleetSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const assignedReg = profile?.assignedVehicleReg;
    if (assignedReg) {
        const assigned = fleet.value.find(v => v.registration?.toUpperCase() === assignedReg.toUpperCase());
        if (assigned) selectedVehicleId.value = assigned.id;
    }
    
    if (!selectedVehicleId.value && fleet.value.length > 0) {
        selectedVehicleId.value = fleet.value[0].id;
    }
  } catch (e) {
    error.value = "Asset Registry synchronisation failure.";
    console.error(e);
  } finally {
    loading.value = false;
  }
};

const handleFileUpload = (event, type) => {
  const file = event.target.files[0];
  if (file) files[type] = file;
};

const submitCheck = async () => {
  if (!isFormValid.value) return;
  isSubmitting.value = true;
  try {
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    const uid = auth.currentUser?.uid || userStore.userId;
    const firstName = userStore.userProfile?.firstName || 'Unknown';
    const lastName = userStore.userProfile?.lastName || 'Personnel';

    if (!cid || !uid || !selectedVehicle.value) throw new Error("Registry context failure.");

    const checkRef = doc(collection(db, 'vehicleChecks'));
    const imageUrls = { external: '', internal: '' };

    for (const type of ['external', 'internal']) {
      if (files[type]) {
        const path = `clients/${cid}/vehicleChecks/${checkRef.id}/${type}.jpg`;
        const fRef = storageRef(storage, path);
        await uploadBytesResumable(fRef, files[type], { contentType: 'image/jpeg' });
        imageUrls[type] = await getDownloadURL(fRef);
      }
    }

    const checkData = {
      clientId: cid,
      vehicleId: selectedVehicle.value.id,
      vehicleReg: selectedVehicle.value.registration.toUpperCase().trim(),
      userId: uid,
      driverName: `${firstName} ${lastName}`.trim(),
      submittedAt: serverTimestamp(),
      data: JSON.parse(JSON.stringify(formData)),
      images: imageUrls,
      status: (hasFailures('external') || hasFailures('internal')) ? 'Fail' : 'Pass'
    };

    await setDoc(checkRef, checkData);
    
    const vehicleRef = doc(db, 'clients', cid, 'vehicles', selectedVehicle.value.id);
    await updateDoc(vehicleRef, { mileage: formData.mileage });

    successMessage.value = true;
    setTimeout(() => { router.push('/dashboard'); }, 3000);
  } catch (e) {
    alert(`Audit Error: ${e.message}`);
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  if (userStore.isAuthReady) fetchFleetData();
  else {
    const unwatch = userStore.$subscribe(() => {
      if (userStore.isAuthReady) { fetchFleetData(); unwatch(); }
    });
  }
});
</script>

<style scoped>
.premium-select {
  @apply block w-full px-6 py-4 bg-white border-2 border-slate-100 rounded-2xl text-slate-700 font-bold transition-all focus:border-[#20FCF1] outline-none shadow-sm appearance-none cursor-pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2338E3B0'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1.25rem center;
  background-size: 1.25rem;
}

.form-input { @apply mt-1 block w-full px-8 py-6 bg-slate-50 border border-slate-200 rounded-[32px] text-sm font-bold text-[#0A1630] shadow-inner focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>